"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback } from "react";
import type { TimeWeightedVotingConfig, TimeUnit } from "./types";
import { convertToMinutes, convertFromMinutes, ensureValueInBounds, parseWholeNumberInput } from "./utils";
import { TimeWeightedToggle, AveragingIntervalInput } from "./components";

/**
 * Props for the TimeWeightedVoting component
 */
interface TimeWeightedVotingProps {
  /** Current configuration for time-weighted voting */
  readonly config: TimeWeightedVotingConfig;
  /** Validation error from the parent wave flow */
  readonly errorMessage?: string | undefined;
  /** Handler called when configuration changes */
  readonly onChange: (config: TimeWeightedVotingConfig) => void;
  /** Whether to show the enable/disable toggle */
  readonly showToggle?: boolean;
}
const getIntervalValidationError = (config: TimeWeightedVotingConfig): string | undefined => {
  if (!config.enabled) {
    return undefined;
  }
  const valueInMinutes = convertToMinutes(config.averagingInterval, config.averagingIntervalUnit);
  if (valueInMinutes < 5) {
    return "Must be at least 5 minutes";
  }
  if (valueInMinutes > 24 * 60) {
    return "Must not exceed 24 hours";
  }
  return undefined;
};

/**
 * TimeWeightedVoting Component
 * Main component that manages the state and orchestrates child components
 * Allows configuration of time-weighted voting settings
 */
export default function TimeWeightedVoting(t0) {
  const $ = _c(30);
  const {
    config,
    errorMessage,
    onChange,
    showToggle: t1
  } = t0;
  const showToggle = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== config.averagingInterval) {
    t2 = () => config.averagingInterval.toString();
    $[0] = config.averagingInterval;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [inputValue, setInputValue] = useState(t2);
  let t3;
  if ($[2] !== config || $[3] !== onChange) {
    t3 = () => {
      onChange({
        ...config,
        enabled: !config.enabled
      });
    };
    $[2] = config;
    $[3] = onChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleToggle = t3;
  let t4;
  if ($[5] !== config || $[6] !== onChange) {
    t4 = value => {
      if (value === "") {
        setInputValue(value);
        return;
      }
      const numValue = parseWholeNumberInput(value);
      if (numValue === null) {
        return;
      }
      setInputValue(value);
      const maxValueForUnit = config.averagingIntervalUnit === "minutes" ? 1440 : 24;
      if (numValue <= maxValueForUnit) {
        onChange({
          ...config,
          averagingInterval: numValue
        });
      } else {
        const cappedValue = maxValueForUnit;
        setInputValue(cappedValue.toString());
        onChange({
          ...config,
          averagingInterval: cappedValue
        });
      }
    };
    $[5] = config;
    $[6] = onChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleIntervalChange = t4;
  let t5;
  if ($[8] !== config || $[9] !== onChange) {
    t5 = unit => {
      if (unit === config.averagingIntervalUnit) {
        return;
      }
      const valueInMinutes = convertToMinutes(config.averagingInterval, config.averagingIntervalUnit);
      const newInterval = ensureValueInBounds(convertFromMinutes(valueInMinutes, unit), unit);
      setInputValue(newInterval.toString());
      onChange({
        ...config,
        averagingInterval: newInterval,
        averagingIntervalUnit: unit
      });
    };
    $[8] = config;
    $[9] = onChange;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleUnitChange = t5;
  let t6;
  if ($[11] !== config || $[12] !== errorMessage) {
    t6 = errorMessage ?? getIntervalValidationError(config);
    $[11] = config;
    $[12] = errorMessage;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const intervalErrorMessage = t6;
  const sectionClassName = showToggle ? "tw-mt-6 tw-border-t tw-border-iron-700 tw-pt-6" : undefined;
  let t7;
  if ($[14] !== config.enabled || $[15] !== handleToggle || $[16] !== showToggle) {
    t7 = showToggle && <TimeWeightedToggle enabled={config.enabled} onToggle={handleToggle} />;
    $[14] = config.enabled;
    $[15] = handleToggle;
    $[16] = showToggle;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== config.averagingIntervalUnit || $[19] !== config.enabled || $[20] !== handleIntervalChange || $[21] !== handleUnitChange || $[22] !== inputValue || $[23] !== intervalErrorMessage || $[24] !== showToggle) {
    t8 = (config.enabled || !showToggle) && <AveragingIntervalInput value={inputValue} unit={config.averagingIntervalUnit} onIntervalChange={handleIntervalChange} onUnitChange={handleUnitChange} validationError={intervalErrorMessage} />;
    $[18] = config.averagingIntervalUnit;
    $[19] = config.enabled;
    $[20] = handleIntervalChange;
    $[21] = handleUnitChange;
    $[22] = inputValue;
    $[23] = intervalErrorMessage;
    $[24] = showToggle;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== sectionClassName || $[27] !== t7 || $[28] !== t8) {
    t9 = <section className={sectionClassName} data-testid="time-weighted-voting">{t7}{t8}</section>;
    $[26] = sectionClassName;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}